import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getDeviceIdFromSession, getSelectedAccountInfo } from '@ibk/shared-auth';
import { config } from '../config';
import { ENABLED_FEATURE_HEADER, getEnabledFeature } from '../developerSettings';
import { selectAppDomain } from '../store/selectors/applicationSelector';
import { selectSelectedAccountId } from '../store/selectors/userSelectors';
import { clearTokens, setTokens } from '../store/slices/tokensSlice';
import { attemptTokenRefresh, createApiError, createAuthHeaders, extractUrl, getTimeout, processTokenFromResponse, shouldAttemptRefresh, } from './authUtils';
import { XApplicationNameEnum } from './types';
export const setTokensAction = setTokens;
export const clearTokensAction = clearTokens;
const DEFAULT_TIMEOUT = 30000;
const API_BASE_URL = config.apiBaseUrl;
const API_BASE_URL_PERSON_ACCESS = config.apiEscritorioPersonAccess;
const isStringArgs = (args) => {
    return typeof args === 'string';
};
const getBaseUrl = (url, args) => {
    const forceAccountantApiBaseUrl = isStringArgs(args)
        ? false
        : args?.forceAccountantApiBaseUrl;
    return forceAccountantApiBaseUrl ? API_BASE_URL_PERSON_ACCESS : API_BASE_URL;
};
const getAccountId = (state, args) => {
    const forceSelectedAccountInfoAccountId = isStringArgs(args)
        ? false
        : args?.forceSelectedAccountInfoAccountId;
    return forceSelectedAccountInfoAccountId ? getSelectedAccountInfo()?.accountId : selectSelectedAccountId(state);
};
const getTransactionIdempotencyId = (args) => {
    return isStringArgs(args) ? undefined : args?.transactionIdempotencyId;
};
const createBaseQuery = (baseUrl, timeout = DEFAULT_TIMEOUT) => {
    return fetchBaseQuery({
        baseUrl,
        timeout,
        prepareHeaders: (headers, { getState, arg }) => {
            const body = typeof arg === 'object' && arg !== null && 'body' in arg ? arg.body : undefined;
            if (!(body instanceof FormData)) {
                headers.set('Content-Type', 'application/json');
            }
            const state = getState();
            const authHeaders = createAuthHeaders(state);
            if (authHeaders.Authorization) {
                headers.set('Authorization', authHeaders.Authorization);
            }
            const accountId = getAccountId(state, arg);
            if (accountId) {
                headers.set('account-id', accountId);
            }
            const deviceId = getDeviceIdFromSession();
            if (deviceId) {
                headers.set('device-id', deviceId);
            }
            const appDomain = selectAppDomain(state);
            if (appDomain) {
                headers.set('X-Application-Name', XApplicationNameEnum[appDomain]);
            }
            const transactionIdempotencyId = getTransactionIdempotencyId(arg);
            if (transactionIdempotencyId) {
                headers.set('transaction-idempotency-id', transactionIdempotencyId);
            }
            const enabledFeature = getEnabledFeature();
            if (enabledFeature) {
                headers.set(ENABLED_FEATURE_HEADER, enabledFeature);
            }
            return headers;
        },
    });
};
export const customBaseQuery = async (args, api, extraOptions) => {
    const url = extractUrl(args);
    const baseUrl = getBaseUrl(url, args);
    const timeout = getTimeout(args, DEFAULT_TIMEOUT);
    const baseQuery = createBaseQuery(baseUrl, timeout);
    const queryArgs = typeof args === 'string' ? url : { ...args, url };
    const result = await baseQuery(queryArgs, api, extraOptions);
    processTokenFromResponse(result.data, api);
    if (result.error) {
        const fetchError = result.error;
        if (shouldAttemptRefresh(fetchError)) {
            const refreshSuccess = await attemptTokenRefresh(api);
            if (refreshSuccess) {
                const refreshedBaseQuery = createBaseQuery(baseUrl, timeout);
                return refreshedBaseQuery(queryArgs, api, extraOptions);
            }
        }
        if ('status' in fetchError) {
            return { error: createApiError(fetchError) };
        }
    }
    return result;
};
export const baseApi = createApi({
    reducerPath: 'baseApi',
    baseQuery: customBaseQuery,
    tagTypes: [
        'Base',
        'Auth',
        'User',
        'PersonAccess',
        'FeatureFlags',
        'VoucherRecharge',
        'PixKeys',
        'ProductManagement',
        'Permissioning',
        'Organization',
        'PinCredentials',
        'Integration',
    ],
    endpoints: () => ({}),
});
